import {
  IOCDataTypes,
  IOCFileTypes,
  IOCGroupedByFileType,
  IOCGroupedByDataType,
  IOCGroup,
  IOCItem,
  IOCFinalGroup,
  IOCDataTypesName,
  StrippedIOCsType,
  mainFileKey,
} from '../../types'
import BaseFormatter from './base'
import { formatEnumItem, has, hasFields } from '../../helpers/functions'
import { hasInterestingIOCs } from '../../helpers'
import { combineSignalGroups } from './helper'

const inputFileOrigin = 'INPUT_FILE'

// Digests of the scanned file, that can be IOCs of it, mapped to IOC types
const digestIOCTypes: { [digest: string]: IOCDataTypes } = {
  MD5: IOCDataTypes.HashesMD5,
  'SHA-1': IOCDataTypes.HashesSHA1,
  'SHA-256': IOCDataTypes.HashesSHA256,
  'SHA-512': IOCDataTypes.HashesSHA512,
}

/**
 * Format data needed for Indicators of Compromise report subpage.
 *
 * Performs grouping of IOCs, so that:
 * - result report has `ioc` item
 * - that item has the following items:
 *   - `mainFile` for main report file, containing all it's IOCs in the form given in report
 *   - `extractedFiles`, containing hash -> IOCs map for all extracted files for given main report file
 *   - `downloadedFiles`, containing hash -> IOCs map for all downloaded files for given main report file
 */
export default class IOCFormatter extends BaseFormatter {
  strippedIocs: StrippedIOCsType = {}

  /**
   * Format original report data
   *
   * @param data
   * @param report
   */
  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    const result: IOCGroupedByFileType = {}
    const fileResource = this.getResource(data, 'file')
    const downloadResource = this.getResource(data, 'file-download')

    let overallUrls: IOCGroup[] = []
    if (downloadResource) {
      overallUrls = this.extractTopUrls(downloadResource)
    }

    if (fileResource) {
      const main: IOCGroupedByDataType = {}
      this.copyIOCFields(fileResource, overallUrls, main, this.getCheckedDigestsIOCs(data, fileResource))

      if (hasFields(main)) {
        result[mainFileKey] = main
      }

      if (has(fileResource, 'extractedFiles')) {
        this.formatFilesIOCs(fileResource.extractedFiles, overallUrls, result, 'extractedFiles')
      }
    }

    if (downloadResource) {
      this.formatFilesIOCs(downloadResource.fileDownloadResults, overallUrls, result, 'downloadedFiles')
    }

    const combinedSignalGroups = combineSignalGroups(data.allSignalGroups, data.resources)

    if (combinedSignalGroups.length > 0) {
      const groupVerdicts = this.formatSignalGroupVerdicts(combinedSignalGroups)

      if (hasFields(groupVerdicts)) {
        report.groupVerdicts = groupVerdicts
      }
    }

    this.refineMetas(result)
    if (hasFields(result)) {
      report.ioc = { data: result }

      if (result[mainFileKey]) {
        report.hasInterestingIOCs = hasInterestingIOCs(result[mainFileKey])
        report.ioc.map = {
          [mainFileKey]: this.createMap(result[mainFileKey]),
        }
      }
    }
    if (hasFields(this.strippedIocs)) {
      // Stripped IOCs are gathered by file hash, but IOCs of the scanned file are
      // addressed by `mainFile` key, so make them available under that key as well
      const mainHash = fileResource?.digests?.['SHA-256']
      if (mainHash && this.strippedIocs[mainHash]) {
        this.strippedIocs[mainFileKey] = this.strippedIocs[mainHash]
      }

      report.strippedIocs = this.strippedIocs
    }
  }

  // Create data structure like {[IOC]: true}, to help quickly detect that given IOC is present in the main file
  createMap(iocData: IOCGroupedByDataType) {
    const map: { [key: string]: boolean } = {}

    Object.keys(iocData).forEach((iocType: string) => {
      const groups: IOCFinalGroup[] = iocData[iocType as IOCDataTypes] ?? []

      groups.forEach((group) => {
        group.references.forEach((item) => {
          map[item.data] = true
        })
      })
    })

    return map
  }

  /**
   * Format IOCs for extracted and downloaded files
   *
   * @param data
   * @param result
   * @param resultField
   */
  formatFilesIOCs(
    data: { [key: string]: any },
    overallUrls: IOCGroup[],
    result: IOCGroupedByFileType,
    resultField: string
  ): void {
    const filesResult: { [hash: string]: IOCGroupedByDataType } = {}

    data.forEach((file: any) => {
      const hash = file.digests['SHA-256']
      const data: IOCGroupedByDataType = {}

      this.copyIOCFields(
        file,
        overallUrls.filter((item) => item.origin.identifier === hash),
        data
      )

      if (hasFields(data)) {
        filesResult[hash] = data
      }
    })

    if (hasFields(filesResult)) {
      const key = resultField as IOCFileTypes
      result[key] = filesResult
    }
  }

  extractTopUrls(data: { [key: string]: any }): IOCGroup[] {
    const field = 'extractedUrls'
    let urls = []
    if (has(data, field)) {
      urls = data[field]
    }

    return urls
  }

  copyIOCFields(
    source: { [key: string]: any },
    overallUrls: IOCGroup[],
    target: IOCGroupedByDataType,
    extraIOCs: { [key in IOCDataTypes]?: IOCGroup[] } = {}
  ): void {
    const urls = overallUrls.filter((item) => item.origin.identifier === source.digests['SHA-256'])

    Object.keys(IOCDataTypes).forEach((key: string) => {
      const iocType = key as keyof typeof IOCDataTypes
      const iocTypeField = IOCDataTypes[iocType]
      const iocTypeName = IOCDataTypesName[iocType]
      const sourceIOCs: IOCGroup[] = has(source, iocTypeField) ? source[iocTypeField] : []
      const extra: IOCGroup[] = extraIOCs[iocTypeField] ?? []

      if (!sourceIOCs.length && !extra.length) {
        return
      }

      // Extra IOCs go first, so that they are never dropped by the amount limit
      const merged = [...extra, ...(iocTypeField === IOCDataTypes.Urls ? urls : []), ...sourceIOCs]
      const limited = this.limitTooManyIOCs(source, merged, iocTypeName)
      const grouped = this.groupIOCs(limited)
      target[iocTypeField] = grouped
    })
  }

  /**
   * Digests of the scanned file are not IOCs on their own. But when a digest has been
   * looked up in OSINT sources, it becomes an IOC, having the verdict of that lookup.
   *
   * Digests without such a lookup are skipped, as there's no information about them.
   * All the data comes from the report, nothing is generated here.
   */
  getCheckedDigestsIOCs(
    data: { [key: string]: any },
    fileResource: { [key: string]: any }
  ): { [key in IOCDataTypes]?: IOCGroup[] } {
    const result: { [key in IOCDataTypes]?: IOCGroup[] } = {}
    const digests = fileResource.digests ?? {}
    const mainHash = digests['SHA-256']

    if (!mainHash) {
      return result
    }

    const checkedResources = this.getOwnCheckedResources(data, mainHash)
    const origin = { type: inputFileOrigin, identifier: mainHash }

    for (const digest in digestIOCTypes) {
      const hash = digests[digest]

      if (!hash || !checkedResources[hash.toLowerCase()]) {
        continue
      }

      result[digestIOCTypes[digest]] = [
        {
          origin,
          // Hash is used as `dataUUID` as well, to have a stable ID to link to this IOC
          references: [{ data: hash, dataUUID: hash, origin }],
        },
      ]
    }

    return result
  }

  /**
   * Gather resources of the scanned file itself, that were looked up in OSINT sources.
   * Providers may report them in a different case, so all of them are lowercased.
   */
  getOwnCheckedResources(data: { [key: string]: any }, mainHash: string): { [resource: string]: boolean } {
    const result: { [resource: string]: boolean } = {}
    const hash = mainHash.toLowerCase()

    for (const osintResource of this.getResources(data, 'osint')) {
      for (const item of osintResource.results ?? []) {
        const isOwnResource =
          item.origin?.type === inputFileOrigin && item.origin?.identifier?.toLowerCase() === hash && item.resource

        if (isOwnResource) {
          result[item.resource.toLowerCase()] = true
        }
      }
    }

    return result
  }

  limitTooManyIOCs(source: { [key: string]: any }, iocs: IOCGroup[], iocTypeName: IOCDataTypesName): IOCGroup[] {
    const limit = 100
    const hash = source.digests['SHA-256']
    const result: IOCGroup[] = []
    let iocTypeCount = 0

    for (const group of iocs) {
      const keepCount = Math.min(group.references.length, limit - iocTypeCount)

      if (keepCount === group.references.length) {
        iocTypeCount += keepCount
        result.push(group)
        continue
      }

      // Mark type as stripped even when nothing of this group fits into the limit,
      // so that user is always notified that not all items are shown
      if (typeof this.strippedIocs[hash] == 'undefined') {
        this.strippedIocs[hash] = {}
      }
      this.strippedIocs[hash][iocTypeName] = true

      if (keepCount > 0) {
        iocTypeCount += keepCount
        result.push({ ...group, references: group.references.slice(0, keepCount) })
      }

      // Limit is reached, all the remaining groups of this type are dropped
      break
    }

    return result
  }

  groupIOCs(originGroups: IOCGroup[]): IOCFinalGroup[] {
    const flattened = this.gatherOrigins(originGroups)
    const grouped = this.groupByOrigins(flattened)

    return grouped
  }

  /**
   * Flatten IOC structure, so that each of them had all sources gathered as array
   */
  gatherOrigins(originGroups: IOCGroup[]): IOCItem[] {
    const result: { [key: string]: IOCItem } = {}

    originGroups.forEach((group: IOCGroup) => {
      group.references.forEach((resource: IOCItem) => {
        const key = resource.data
        const item = result[key] ? result[key] : { ...resource, origins: [] }
        const originType = formatEnumItem(group.origin.type)

        const httpStatusCode = resource.metaData?.httpStatusCode
        if (httpStatusCode) {
          item.httpStatusCode = httpStatusCode
        }

        item.origins && !item.origins.includes(originType) && item.origins.push(originType)

        if (!result[key]) {
          result[key] = item
        }
      })
    })

    return Object.values(result)
  }

  /**
   * Again group IOCs, but now by the whole array of origins each IOC has
   */
  groupByOrigins(items: IOCItem[]): IOCFinalGroup[] {
    const result: { [key: string]: IOCFinalGroup } = {}

    items.forEach((item: IOCItem) => {
      // Shouldn't happen, just to pass TS type check
      if (!item.origins) {
        return
      }

      item.origins.sort((a, b) => a.localeCompare(b))
      const key = item.origins.join('|')

      if (!result[key]) {
        result[key] = {
          origins: item.origins,
          references: [],
        }
      }

      result[key].references.push(item)
    })

    return Object.values(result)
  }

  refineMetas(result: IOCGroupedByFileType): void {
    for (const key in result) {
      if (key === IOCFileTypes.Main) {
        this.refineFileMeta(result[IOCFileTypes.Main])
      } else {
        const fileResults: { [hash: string]: IOCGroupedByDataType } = result[key as IOCFileTypes]
        for (const hash in fileResults) {
          this.refineFileMeta(fileResults[hash])
        }
      }
    }
  }

  refineFileMeta(ioc: IOCGroupedByDataType): void {
    for (const key in ioc) {
      const groups: IOCFinalGroup[] = ioc[key as IOCDataTypes] ?? []
      groups.forEach((group) => {
        group.references &&
          group.references.forEach((ref: IOCItem) => {
            if (!ref.metaData) return

            ref.metaData = this.cleanMeta(ref.metaData)
          })
      })
    }
  }

  cleanMeta(metaData: { [key: string]: any }): { [key: string]: any } | undefined {
    const result = metaData
    if (result.reason && Array.isArray(result.reason)) {
      result.reason = result.reason.filter((item) => {
        return item !== 'INTERNAL'
      })

      if (result.reason.length === 0) delete result.reason
    }

    if (result.referenceIDs) {
      delete result.referenceIDs
    }

    if (result.httpStatusCode) {
      delete result.httpStatusCode
    }

    if (Object.keys(result).length > 0) {
      return result
    }
  }

  formatSignalGroupVerdicts(data: any[]): { [key: string]: string } {
    const result: { [key: string]: string } = {}
    for (const group of data) {
      if (!group.identifier || !group.verdict?.verdict) {
        continue
      }

      result[group.identifier] = group.verdict.verdict.toLowerCase()
    }

    return result
  }
}
